import React, { useRef } from 'react';

import isEmpty from 'lodash/isEmpty';

import Button from '@gogame/ui/Button';
import Stack from '@gogame/ui/Stack';
import Tooltip from '@gogame/ui/Tooltip';

import { UploadFileIcon } from '../Icons';
import ErrorText from './components/ErrorText';
import FileItem from './components/FileItem';
import HelperText from './components/HelperText';

const resetFileInput = (e) => {
  e.persist();
  e.target.value = '';
};

const defaultGetFileLabel = ({ name }, info) => name || info?.name;

const defaultGetFileKey = ({ id }) => id;

const FileInput = ({
  dataCy,
  label,
  value,
  error,
  helper,
  accept,
  multiple,
  tooltip,
  tooltipPlacement = 'top',
  variant = 'text',
  icon = <UploadFileIcon />,
  color = 'primary',
  getFileLabel = defaultGetFileLabel,
  getFileKey = defaultGetFileKey,
  onUpload,
  onRemove,
  loading,
  disabled,
  direction = 'row',
  fileLabels = true,
  children,
  ...props
}) => {
  const labelEl = useRef(null);

  const simulateLabelClick = (e) => {
    e.preventDefault();
    if (labelEl.current) {
      labelEl.current.click();
    }
  };

  const filesInfoRef = useRef({});

  const handleUpload = (e) => {
    const files = Array.from(e.target.files);
    resetFileInput(e);
    onUpload?.(e, files).then((response) => {
      if (!response) {
        return;
      }

      if (multiple) {
        files.forEach((file, i) => {
          const key = getFileKey(response[i]);
          filesInfoRef.current[key] = { name: file.name };
        });
      } else {
        const key = getFileKey(response);
        filesInfoRef.current[key] = { name: files[0].name };
      }
    });
  };

  const handleRemove = (e, item) => {
    onRemove?.(e, item);
  };

  return (
    <Stack spacing={0.5} flex={'1 1 auto'} zeroMinWidth={true}>
      <Stack spacing={2} direction={direction}>
        <Stack flex={'0 0 auto'}>
          <label data-cy={dataCy} ref={labelEl}>
            <input
              type={'file'}
              accept={accept}
              multiple={multiple}
              onChange={handleUpload}
              hidden={true}
              disabled={disabled || loading}
            />
            <Tooltip
              title={tooltip}
              arrow={true}
              placement={tooltipPlacement}
              wrapper={true}
            >
              <Button
                variant={variant}
                icon={icon}
                onClick={simulateLabelClick}
                color={color}
                loadingPosition={'start'}
                loading={loading}
                disabled={disabled}
                {...props}
              >
                {label}
              </Button>
            </Tooltip>
          </label>
        </Stack>
        {fileLabels && !isEmpty(value) ? (
          <Stack
            flex={'1 1 auto'}
            justifyContent={'center'}
            zeroMinWidth={true}
          >
            <Stack
              direction={'row'}
              alignItems={'center'}
              flexWrap={'wrap'}
              m={-0.5}
            >
              {multiple ? (
                value.map((item, i) => (
                  <FileItem
                    key={i}
                    label={getFileLabel(
                      item,
                      filesInfoRef.current[getFileKey(item)]
                    )}
                    onDelete={(e) => handleRemove(e, item)}
                    disabled={disabled}
                  />
                ))
              ) : (
                <FileItem
                  label={getFileLabel(
                    value,
                    filesInfoRef.current[getFileKey(value)]
                  )}
                  onDelete={handleRemove}
                  disabled={disabled}
                />
              )}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
      {helper || error ? (
        <Stack spacing={0.5} pl={1.75} pr={1.75}>
          {helper ? <HelperText>{helper}</HelperText> : null}
          {error ? <ErrorText>{error}</ErrorText> : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default FileInput;
